// Admin API Service for interacting with the creator app's backend
export class AdminApiService {
  private readonly baseUrl: string
  private userId: string | null = null

  constructor(baseUrl?: string) {
    // Use environment variable if available, otherwise fallback to parameter or localhost
    this.baseUrl = baseUrl || 
                   (import.meta.env.VITE_API_BASE_URL as string) || 
                   'http://localhost:8787/api'
  }

  setUserId(userId: string) {
    this.userId = userId
  }

  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json'
    }
    
    if (this.userId) {
      headers['x-user-id'] = this.userId
    }
    
    return headers
  }

  // Data Browser - D1 Database Operations
  async getUsers() {
    const response = await fetch(`${this.baseUrl}/admin/users`, {
      headers: this.getHeaders()
    })
    if (!response.ok) {
      throw new Error(`Failed to fetch users: ${response.statusText}`)
    }
    return response.json()
  }

  async getBooks() {
    const response = await fetch(`${this.baseUrl}/books`, {
      headers: this.getHeaders()
    })
    if (!response.ok) {
      throw new Error(`Failed to fetch books: ${response.statusText}`)
    }
    return response.json()
  }

  async getChapters(bookId?: string): Promise<any> {
    if (bookId) {
      const response = await fetch(`${this.baseUrl}/books/${bookId}/chapters`, { 
        headers: this.getHeaders() 
      })
      if (!response.ok) {
        throw new Error(`Failed to fetch chapters: ${response.statusText}`)
      }
      return response.json()
    }
    
    // To get all chapters, we need to fetch books first then get chapters for each
    const books = await this.getBooks()
    const allChapters = []
    
    for (const book of books) {
      try {
        const chapters: any = await this.getChapters(book.id)
        allChapters.push(...chapters.map((chapter: any) => ({
          ...chapter,
          book_title: book.title,
          book_id: book.id
        })))
      } catch (error) {
        console.warn(`Failed to fetch chapters for book ${book.id}:`, error)
      }
    }
    
    return allChapters
  }

  async getApiUsage() {
    const response = await fetch(`${this.baseUrl}/admin/api-usage`, {
      headers: this.getHeaders()
    })
    if (!response.ok) {
      throw new Error(`Failed to fetch API usage: ${response.statusText}`)
    }
    return response.json()
  }

  // File Browser - R2 Storage Operations
  async getR2Files(prefix: string = '') {
    const url = new URL(`${this.baseUrl}/admin/r2/files`)
    if (prefix) {
      url.searchParams.set('prefix', prefix)
    }
    
    const response = await fetch(url.toString(), {
      headers: this.getHeaders()
    })
    if (!response.ok) {
      throw new Error(`Failed to fetch R2 files: ${response.statusText}`)
    }
    return response.json()
  }

  async getR2FileInfo(key: string) {
    // Individual file info - for now return basic info since we don't have a specific endpoint
    // The files list already contains the key, size, lastModified, and etag
    return {
      key,
      contentType: key.endsWith('.png') ? 'image/png' : 
                   key.endsWith('.mp3') ? 'audio/mpeg' : 'application/octet-stream'
    }
  }

  // Role Management
  async getAuthStatus() {
    const headers = this.getHeaders()
    // For auth status, also send userId as Authorization header for backend compatibility
    if (this.userId) {
      (headers as any)['Authorization'] = `Bearer ${this.userId}`
    }
    
    const response = await fetch(`${this.baseUrl}/admin/auth/status`, {
      headers
    })
    if (!response.ok) {
      throw new Error(`Failed to get auth status: ${response.statusText}`)
    }
    return response.json()
  }

  async updateUserRole(userId: string, role: string, reason?: string) {
    const response = await fetch(`${this.baseUrl}/admin/users/${userId}/role`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify({ role, reason })
    })
    if (!response.ok) {
      throw new Error(`Failed to update user role: ${response.statusText}`)
    }
    return response.json()
  }

  // System information
  async getSystemInfo() {
    const headers = this.getHeaders()
    // For system info, also send userId as Authorization header for backend compatibility
    if (this.userId) {
      (headers as any)['Authorization'] = `Bearer ${this.userId}`
    }
    
    const response = await fetch(`${this.baseUrl}/admin/system-info`, {
      headers
    })
    if (!response.ok) {
      throw new Error(`Failed to get system info: ${response.statusText}`)
    }
    return response.json()
  }

  // Health check
  async healthCheck() {
    const response = await fetch(`${this.baseUrl}/health`)
    return response.ok
  }
}